const COORD_PRECISION = 1e5; // 5 decimal places
const SHAPE_COORD_PRECISION = 1e6; // 6 decimal places for shape coordinates

export const MAP_VIEWPORT_FILTER_FIELDS = [
  'mapZoom',
  'mapBoundsTopLeft',
  'mapBoundsBottomRight',
];

export const MAP_FILTER_FIELDS = [
  ...MAP_VIEWPORT_FILTER_FIELDS,
  'mapShapeType',
  'mapShapeRadius',
  'mapShapePoints',
];

export function isMapFilter(field) {
  return MAP_FILTER_FIELDS.includes(field);
}

function roundShapeCoord(val) {
  return Math.round(val * SHAPE_COORD_PRECISION) / SHAPE_COORD_PRECISION;
}

function formatShapePoint(lat, lng) {
  return `${roundShapeCoord(lat)}, ${roundShapeCoord(lng)}`;
}

/**
 * Encode map viewport to filter values that match API parameters.
 *
 * @param {Object} viewport - Viewport object with zoom and bounds
 * @param {number} viewport.zoom - Zoom level
 * @param {Object} viewport.bounds - Bounds object
 * @param {Object} viewport.bounds.topLeft - Top left coordinate {lat, lng}
 * @param {Object} viewport.bounds.bottomRight - Bottom right coordinate {lat, lng}
 * @returns {Object} Object with zoom, topLeft, and bottomRight as strings
 */
export function encodeMapBounds(viewport) {
  const { zoom, bounds } = viewport;
  const tlLat =
    Math.round(bounds.topLeft.lat * COORD_PRECISION) / COORD_PRECISION;
  const tlLng =
    Math.round(bounds.topLeft.lng * COORD_PRECISION) / COORD_PRECISION;
  const brLat =
    Math.round(bounds.bottomRight.lat * COORD_PRECISION) / COORD_PRECISION;
  const brLng =
    Math.round(bounds.bottomRight.lng * COORD_PRECISION) / COORD_PRECISION;

  return {
    zoom: zoom.toString(),
    topLeft: `${tlLat},${tlLng}`,
    bottomRight: `${brLat},${brLng}`,
  };
}

/**
 * Decode map filter values back to viewport object.
 *
 * @param {string} zoom - Zoom level as string
 * @param {string} topLeft - "lat,lng" string
 * @param {string} bottomRight - "lat,lng" string
 * @returns {Object|null} Viewport object or null if invalid
 */
export function decodeMapBounds(zoom, topLeft, bottomRight) {
  if (!zoom || !topLeft || !bottomRight) return null;

  const zoomNum = Number(zoom);
  const tlParts = topLeft.split(',').map(Number);
  const brParts = bottomRight.split(',').map(Number);

  if (
    isNaN(zoomNum) ||
    tlParts.length !== 2 ||
    brParts.length !== 2 ||
    tlParts.some(isNaN) ||
    brParts.some(isNaN)
  ) {
    return null;
  }

  return {
    zoom: zoomNum,
    bounds: {
      topLeft: { lat: tlParts[0], lng: tlParts[1] },
      bottomRight: { lat: brParts[0], lng: brParts[1] },
    },
  };
}

/**
 * Extract map bounds from React Search UI filters array.
 *
 * @param {Array} filters - React Search UI filters array
 * @returns {Object|null} Decoded viewport object or null
 */
export function getMapBoundsFromFilters(filters) {
  if (!filters) return null;

  const zoomFilter = filters.find(f => f.field === 'mapZoom');
  const topLeftFilter = filters.find(f => f.field === 'mapBoundsTopLeft');
  const bottomRightFilter = filters.find(
    f => f.field === 'mapBoundsBottomRight',
  );

  if (!zoomFilter || !topLeftFilter || !bottomRightFilter) {
    return null;
  }

  return decodeMapBounds(
    zoomFilter.values?.[0],
    topLeftFilter.values?.[0],
    bottomRightFilter.values?.[0],
  );
}

/**
 * Read map bounds directly from URL query params.
 *
 * @returns {Object|null} Decoded viewport object or null
 */
export function getMapBoundsFromURL() {
  const params = new URLSearchParams(window.location.search);
  const zoom = params.get('mapZoom');
  const topLeft = params.get('mapBoundsTopLeft');
  const bottomRight = params.get('mapBoundsBottomRight');
  return decodeMapBounds(zoom, topLeft, bottomRight);
}

/**
 * Encode a drawn shape into filter values for the API.
 *
 * @param {Object} shape - Shape object from DrawControl
 * @returns {Object|null} { shapeType, shapeRadius, shapePoints[] } or null
 */
export function encodeShapeFilters(shape) {
  if (!shape) {
    return null;
  }

  switch (shape.type) {
    case 'circle': {
      const [lat, lng] = shape.metadata.center;

      return {
        shapeType: 'circle',
        shapeRadius: `${Math.round(shape.metadata.radiusMeters)}m`,
        shapePoints: [formatShapePoint(lat, lng)],
      };
    }

    case 'rectangle': {
      const [[south, west], [north, east]] = shape.metadata.bounds;

      return {
        shapeType: 'rectangle',
        shapeRadius: null,
        shapePoints: [
          formatShapePoint(north, west),
          formatShapePoint(south, east),
        ],
      };
    }

    case 'polygon':
    case 'buffered-line': {
      const coords = shape.geojson.geometry.coordinates[0];

      return {
        shapeType: 'polygon',
        shapeRadius: null,
        shapePoints: coords.map(([lng, lat]) => formatShapePoint(lat, lng)),
      };
    }

    default:
      return null;
  }
}

/**
 * Parse a shape point string "lat, lng" into [lat, lng] numbers.
 *
 * @param {string} pointStr - "lat, lng" or "lat,lng"
 * @returns {number[]|null} [lat, lng] or null if invalid
 */
export function parseShapePoint(pointStr) {
  if (!pointStr) {
    return null;
  }

  const parts = pointStr.split(',').map(s => Number(s.trim()));

  if (parts.length !== 2 || parts.some(isNaN)) {
    return null;
  }

  return parts;
}

/**
 * Extract shape filter values from Search UI filters array.
 *
 * @param {Array} filters - React Search UI filters array
 * @returns {Object|null} { shapeType, shapeRadius, shapePoints[] } or null
 */
export function getShapeFiltersFromFilters(filters) {
  if (!filters) {
    return null;
  }

  const typeFilter = filters.find(f => f.field === 'mapShapeType');

  if (!typeFilter) {
    return null;
  }

  const radiusFilter = filters.find(f => f.field === 'mapShapeRadius');
  const pointsFilter = filters.find(f => f.field === 'mapShapePoints');

  if (!pointsFilter || pointsFilter.values.length === 0) {
    return null;
  }

  return {
    shapeType: typeFilter.values[0],
    shapeRadius: radiusFilter?.values?.[0] || null,
    shapePoints: pointsFilter.values,
  };
}
